import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { CheckCircle, CurrencyDollar, Wallet, ShieldCheck, Rocket } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fallback used while loading or if the public-settings endpoint fails. Mirrors
// the backend default in services/pricing.py + .env (PLATFORM_FEE_PERCENT=17).
const FALLBACK_FEE_PCT = 17;

export default function BecomeSeller() {
  const [feePct, setFeePct] = useState(FALLBACK_FEE_PCT);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/settings/public`)
      .then(({ data }) => {
        if (cancelled) return;
        const v = Number(data?.platform_fee_percent);
        if (Number.isFinite(v) && v > 0 && v < 100) setFeePct(v);
      })
      .catch(() => { /* leave fallback in place */ });
    return () => { cancelled = true; };
  }, []);

  const keepPct = Math.max(0, 100 - feePct);
  const feeLabel = `${feePct}%`;
  const keepLabel = `${keepPct}%`;

  const benefits = [
    "Reach businesses ready to buy",
    `Keep ${keepLabel} — flat ${feeLabel} platform fee`,
    "Structured listings reduce scope creep",
    "Escrow + admin disputes protect you",
    "Tier system rewards quality work",
  ];

  // Worked examples — derived from the live fee so the math always matches the copy.
  const examples = [
    { sale: 100, fee: Number((100 * feePct / 100).toFixed(2)), keep: Number((100 * keepPct / 100).toFixed(2)) },
    { sale: 500, fee: Number((500 * feePct / 100).toFixed(2)), keep: Number((500 * keepPct / 100).toFixed(2)) },
  ];

  return (
    <div>
      <section className="relative overflow-hidden bg-mv-secondary text-white">
        <div className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full bg-mv-primary/40 blur-3xl" />
        <div className="absolute -bottom-32 -right-32 w-[420px] h-[420px] rounded-full bg-mv-accent/20 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-6 lg:px-8 py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-mv-accent/15 text-mv-accent text-xs font-bold uppercase tracking-[0.2em] mb-6">
              <Rocket size={14} weight="duotone" /> For sellers
            </div>
            <h1 className="font-display font-black text-4xl lg:text-6xl tracking-tight leading-tight">
              Sell your expertise.<br/><span className="text-mv-accent">Keep more of what you earn.</span>
            </h1>
            <p className="mt-5 text-white/80 max-w-lg" data-testid="seller-hero-subtitle">
              MarketVyne is built for serious digital service providers. A flat {feeLabel} fee, transparent risk labels, structured listings, and a fair dispute process.
            </p>
            <div className="mt-8 flex gap-3 flex-wrap">
              <Link to="/register?role=seller" data-testid="seller-cta-register">
                <Button className="bg-mv-accent hover:bg-lime-400 text-mv-secondary font-black rounded-xl px-7 py-4 h-auto">
                  Apply as a seller
                </Button>
              </Link>
              <Link to="/how-it-works">
                <Button variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white hover:text-mv-secondary rounded-xl font-bold px-7 py-4 h-auto">
                  How it works
                </Button>
              </Link>
            </div>
          </div>
          <img className="rounded-3xl w-full object-cover h-[420px] border border-white/10"
            src="https://static.prod-images.emergentagent.com/jobs/cdc1b282-45f1-44b5-8dce-1787d2003dd7/images/b6a4cfca41d9e5fcd7f74dd8eaeb99b5ed7681148559970502c69b584ab94375.png" alt="" />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-6 lg:px-8 py-20 grid md:grid-cols-2 gap-10 items-start">
        <div className="min-w-0">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-mv-primary mb-2">Why sell here</div>
          <h2 className="font-display font-bold text-3xl text-mv-secondary">A marketplace that respects experts.</h2>
          <ul className="mt-6 space-y-3" data-testid="seller-benefits-list">
            {benefits.map((b) => (
              <li key={b} className="flex items-center gap-3 text-gray-700">
                <CheckCircle weight="duotone" className="text-mv-primary" /> {b}
              </li>
            ))}
          </ul>

          <div className="mt-8 rounded-2xl border border-gray-200 bg-white p-5" data-testid="seller-fee-examples">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-mv-primary mb-3">
              What you keep
            </div>
            <div className="space-y-3 text-sm">
              {examples.map(({ sale, fee, keep }) => (
                <div key={sale} className="flex items-baseline justify-between gap-4 border-b border-gray-100 last:border-0 pb-3 last:pb-0">
                  <div className="text-gray-700">${sale} sale</div>
                  <div className="text-right">
                    <div className="text-gray-500 text-xs">MarketVyne fee: <span className="font-semibold text-gray-700">${fee}</span></div>
                    <div className="font-display font-black text-mv-secondary">Seller keeps: ${keep}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { i: CurrencyDollar, t: `Flat ${feeLabel} fee`, d: "No tier penalties. No hidden cuts.", testid: "seller-fee-card" },
            { i: Wallet, t: "Fast payouts", d: "Stripe Connect for the MVP." },
            { i: ShieldCheck, t: "Dispute fairness", d: "Admin reviews scope, delivery, and chat." },
            { i: Rocket, t: "Tier system", d: "Move from New → Verified → Certified." },
          ].map(({ i: Icon, t, d, testid }) => (
            <div key={t} className="bg-white rounded-2xl p-5 border border-gray-200" data-testid={testid}>
              <Icon size={22} weight="duotone" className="text-mv-primary mb-3" />
              <div className="font-display font-bold text-mv-secondary">{t}</div>
              <p className="text-sm text-gray-600">{d}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
